import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { EventDetailStats, LINK_TYPE_LABELS } from '@radevents/shared';
import eventService from '../services/eventService';
import { LockIcon } from './organizer/manage/chrome';

/**
 * Die Zahlen zu diesem Event, direkt auf seiner Seite (Trello „Event-Detail-Seite überarbeiten",
 * 07.09.2026).
 *
 * Sichtbar für Admin und Moderator immer, für den Verwalter des Veranstalters mit Premium; ohne
 * Premium steht an derselben Stelle, **was** fehlt (`locked`) — derselbe Haken wie am Termin im
 * Veranstalter-Bereich, hier nur einmal je Seite. Die Regel sitzt im Server
 * (`EventDetailStatsService`); die Seite entscheidet nur, ob sie fragt.
 *
 * Dieselben Wörter wie im Reiter *Statistiken* (Aufrufe · Besucher · Auf deinen Link geklickt ·
 * Zum Kalender hinzugefügt), sonst gäbe es zwei Sprachen für dieselbe Sache. Für die Redaktion
 * heißt es „den Link" — „deinen" wäre dort falsch.
 */
interface Props {
  eventId: number;
  audience: 'redaktion' | 'veranstalter';
  locked?: boolean;
  /** Ziel des Premium-Hinweises: die Veranstalterseite, dort liegt der Dialog. */
  organizerId?: number | null;
}

const CARD: React.CSSProperties = { background: '#fff', border: '1px solid #E5E1E1', borderRadius: 14, padding: 20 };
const MONO_LABEL: React.CSSProperties = { fontFamily: "'Space Mono',monospace", fontSize: 12, letterSpacing: '0.12em', color: '#2C2C2C', textTransform: 'uppercase' };
const MONO_STAT: React.CSSProperties = { fontFamily: "'Space Mono',monospace", fontSize: 9.5, letterSpacing: '0.1em', color: '#9A9494', textTransform: 'uppercase', marginTop: 6, display: 'block' };
const BIG: React.CSSProperties = { display: 'block', fontFamily: "'Archivo',sans-serif", fontWeight: 800, fontSize: 22, lineHeight: 1, color: '#2C2C2C', fontVariantNumeric: 'tabular-nums' };

/**
 * Wohin ein Klick ging, in Worten. Web und App melden dasselbe Ziel unter verschiedenen
 * Schlüsseln („veranstalter-seite" bzw. „veranstalter", großer Knopf „anmeldung" bzw. Listeneintrag
 * „link:REGISTRATION") — die Zeile fasst sie zusammen, sonst stünde „Anmeldung" zweimal.
 */
export function clickTargetLabel(context: string): string {
  switch (context) {
    case 'anmeldung': case 'link:REGISTRATION': return 'Anmeldung';
    case 'ausschreibung': case 'link:ANNOUNCEMENT': return 'Ausschreibung';
    case 'veranstalter-seite': case 'veranstalter': case 'link:WEBSITE': return 'Veranstalter-Seite';
    case 'flyer': return 'Flyer';
    case 'unterkunft': return 'Unterkunft';
    case 'ical': return 'Kalender';
    case '': return 'Sonstiges';
    default:
      return context.startsWith('link:')
        ? (LINK_TYPE_LABELS[context.slice(5)] ?? context.slice(5))
        : context;
  }
}

export function mergedClickTargets(stats: EventDetailStats): { label: string; clicks: number }[] {
  const byLabel = new Map<string, number>();
  for (const t of stats.clickTargets) {
    const label = clickTargetLabel(t.context);
    byLabel.set(label, (byLabel.get(label) ?? 0) + t.clicks);
  }
  return Array.from(byLabel.entries())
    .map(([label, clicks]) => ({ label, clicks }))
    .sort((a, b) => b.clicks - a.clicks);
}

/** Ein Datum wie „03.05.2026" — die Karte hat nur diese eine Form. */
function dateLabel(iso: string): string {
  return new Date(iso + 'T00:00:00').toLocaleDateString('de-DE');
}

/**
 * Frühere Auflagen mit ihren Zahlen (21.09.2026). Jede Zeile ist ein eigenes Zeitfenster auf
 * denselben Klicks, nichts wurde kopiert. Eine Auflage aus der Zeit vor der Messung bekommt
 * keine Nullen, sondern das Wort dafür: eine Null hier wäre eine Behauptung über das Jahr.
 */
function PreviousEditions({ stats, clickLabel }: { stats: EventDetailStats; clickLabel: string }) {
  if (stats.previousEditions.length === 0) return null;
  // MONO_STAT ist `display: block` (steht unter einer Zahl) — in der Tabelle muss es die Zelle bleiben.
  const head: React.CSSProperties = { ...MONO_STAT, display: 'table-cell', marginTop: 0, textAlign: 'right', fontWeight: 400, padding: '0 0 6px 12px', verticalAlign: 'bottom' };
  const cell: React.CSSProperties = { fontFamily: "'Archivo',sans-serif", fontWeight: 700, fontSize: 14, color: '#2C2C2C', textAlign: 'right', padding: '6px 0 6px 12px', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' };
  return (
    <div style={{ padding: '14px 20px 16px', borderTop: '1px solid #E8E4F0' }} data-testid="event-stats-editions">
      <div style={{ ...MONO_LABEL, fontSize: 11, marginBottom: 8 }}>FRÜHERE AUFLAGEN</div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ borderCollapse: 'collapse', width: '100%' }}>
          <thead>
            <tr>
              <th style={{ ...head, textAlign: 'left', paddingLeft: 0 }}>Termin</th>
              <th style={head}>Aufrufe</th>
              <th style={head}>Besucher</th>
              <th style={head}>{clickLabel}</th>
              <th style={head}>Zum Kalender</th>
              <th style={head}>Vorgemerkt</th>
            </tr>
          </thead>
          <tbody>
            {stats.previousEditions.map(e => (
              <tr key={e.eventDate} style={{ borderTop: '1px solid #F0EAEC' }}>
                <td style={{ ...cell, textAlign: 'left', paddingLeft: 0, fontFamily: "'Hanken Grotesk',sans-serif", fontWeight: 600 }}>{dateLabel(e.eventDate)}</td>
                {e.since === null ? (
                  <td colSpan={4} style={{ ...cell, fontFamily: "'Hanken Grotesk',sans-serif", fontWeight: 400, color: '#9A9494', textAlign: 'left' }}>
                    vor Beginn der Messung, keine Zahlen
                  </td>
                ) : (
                  <>
                    <td style={cell}>{e.views}</td>
                    <td style={cell}>{e.uniqueViewers}</td>
                    <td style={{ ...cell, color: '#922C57' }}>{e.clicks}</td>
                    <td style={cell}>{e.calendarAdds}</td>
                  </>
                )}
                {/* Nachgetragene Auflagen kennen den Stand der Vormerkungen nicht — eine 0 wäre eine Behauptung. */}
                <td style={cell}>{e.since === null ? '–' : e.favorites}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/**
 * Die Karte mit fertigen Zahlen — ohne Laden, damit das Design-Lab sie mit Beispieldaten zeigen
 * kann. `EventStatsCard` darunter holt die Zahlen und hängt sie hier ein.
 */
export const EventStatsCardView: React.FC<{ stats: EventDetailStats; audience: Props['audience'] }> = ({ stats, audience }) => {
  const clickLabel = audience === 'veranstalter' ? 'Auf deinen Link geklickt' : 'Auf den Link geklickt';
  const targets = mergedClickTargets(stats);
  // Gibt es frühere Auflagen, sagt die Kopfzeile, für welche die Zahlen gelten: „seit" allein
  // hieße sonst „seit Beginn der Messung", und genau das stimmt dann nicht mehr.
  const scope = stats.previousEditions.length > 0 ? `Auflage ${dateLabel(stats.editionDate)}` : null;
  const sinceLabel = stats.since ? `seit ${dateLabel(stats.since)}` : 'noch keine Aufrufe erfasst';
  // Aufrufe und Besucher in einer Kachel: sie beschreiben dasselbe (wer die Seite sah), und mit
  // sechs Kacheln brach die Reihe um und ließ eine einzelne Kachel in der zweiten Zeile stehen.
  const metrics: { value: number; label: string; accent?: boolean; second?: { value: number; label: string } }[] = [
    { value: stats.views, label: 'Aufrufe', second: { value: stats.uniqueViewers, label: 'Besucher' } },
    { value: stats.clicks, label: clickLabel, accent: true },
    { value: stats.calendarAdds, label: 'Zum Kalender hinzugefügt' },
    { value: stats.favorites, label: 'Vorgemerkt' },
  ];
  // Die Glocke steht nur, solange die Anmeldung nicht offen ist; bei den meisten Events gab es sie
  // nie. Eine Null hieße dort nicht „niemand wollte“, deshalb erst ab dem ersten Klick.
  if (stats.registrationAlerts) {
    metrics.push({ value: stats.registrationAlerts, label: 'Lassen sich benachrichtigen' });
  }

  return (
    <div style={{ ...CARD, padding: 0, overflow: 'hidden' }} data-testid="event-stats">
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: '4px 12px', padding: '15px 20px', borderBottom: '1px solid #E8E4F0' }}>
        <span style={MONO_LABEL}>ZAHLEN</span>
        <span style={{ fontFamily: "'Hanken Grotesk',sans-serif", fontSize: 13, color: '#9A9494' }}>
          {scope ? `${scope}, ${sinceLabel}` : sinceLabel}
        </span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(112px, 1fr))', gap: 1, background: '#F0EAEC' }}>
        {metrics.map(m => (
          <div key={m.label} style={{ background: '#fff', padding: '14px 20px' }}>
            {m.second ? (
              <>
                <b style={BIG}>{m.value}<span style={{ color: '#9A9494', fontWeight: 700 }}> / {m.second.value}</span></b>
                <span style={MONO_STAT}>{m.label} / {m.second.label}</span>
              </>
            ) : (
              <>
                <b style={{ ...BIG, color: m.accent ? '#922C57' : BIG.color }}>{m.value}</b>
                <span style={{ ...MONO_STAT, color: m.accent ? '#922C57' : MONO_STAT.color }}>{m.label}</span>
              </>
            )}
          </div>
        ))}
      </div>
      {targets.length > 0 && (
        <div style={{ padding: '11px 20px', borderTop: '1px solid #E8E4F0', fontFamily: "'Space Mono',monospace", fontSize: 11, letterSpacing: '0.04em', color: '#6A6A6A' }}>
          Klicks: {targets.map((t, i) => (
            <React.Fragment key={t.label}>
              {i > 0 && ' · '}
              <span style={{ color: '#2C2C2C' }}>{t.label} {t.clicks}</span>
            </React.Fragment>
          ))}
        </div>
      )}
      <PreviousEditions stats={stats} clickLabel={clickLabel} />
    </div>
  );
};

const EventStatsCard: React.FC<Props> = ({ eventId, audience, locked = false, organizerId }) => {
  const [stats, setStats] = useState<EventDetailStats | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setStats(null);
    setFailed(false);
    if (locked) return;
    let cancelled = false;
    eventService.getStats(eventId)
      .then(s => { if (!cancelled) setStats(s); })
      // 402/404: die Seite hat sich verschätzt — dann lieber keine Karte als eine falsche.
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => { cancelled = true; };
  }, [eventId, locked]);

  const clickLabel = audience === 'veranstalter' ? 'Auf deinen Link geklickt' : 'Auf den Link geklickt';

  if (locked) {
    return (
      <div style={CARD} data-testid="event-stats-locked">
        <div style={{ ...MONO_LABEL, marginBottom: 10 }}>ZAHLEN</div>
        <p style={{ fontFamily: "'Hanken Grotesk',sans-serif", fontSize: 14, lineHeight: 1.5, color: '#6A6A6A', margin: '0 0 12px' }}>
          Die Zahlen zu diesem Event gehören zu Premium.
        </p>
        <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexWrap: 'wrap', gap: '8px 18px' }}>
          {['Aufrufe', 'Besucher', clickLabel, 'Zum Kalender hinzugefügt'].map(label => (
            <li key={label} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, ...MONO_STAT, marginTop: 0 }}>
              <span style={{ color: '#C4BFBF', display: 'inline-flex' }}><LockIcon /></span>
              {label}
            </li>
          ))}
        </ul>
        {organizerId != null && (
          <Link to={`/veranstalter/${organizerId}`}
            style={{ display: 'inline-block', marginTop: 14, fontWeight: 700, fontSize: 13, color: '#922C57', textDecoration: 'none' }}>
            Premium freischalten ›
          </Link>
        )}
      </div>
    );
  }

  if (failed || !stats) return null;

  return <EventStatsCardView stats={stats} audience={audience} />;
};

export default EventStatsCard;
